import MiscApi from '@/services/api/misc.js'
import { mapGetters } from 'vuex'
import BrandApi from '@/services/api/brandApi.js'
import metaTags from '@/services/metaTags'
import sanitizeHtml from 'sanitize-html'

const MiscApiService = new MiscApi(),
  BrandApiService = new BrandApi(),
  trimLimit = 400

export default {
  props: {
    brand: Object,
    eventId: String,
    IsABrandUserOnTheirOwnBrandPage: Boolean,
    brandVanityUrlName: String,
    isRetailer: Boolean,
    showBrandDetailsOnly: Boolean,
    showNoProductsSectionOnly: Boolean,
    forceRetailerView: Boolean,
    canShowTradeShowInfo: Boolean,
    hasProducts: Boolean
  },
  data() {
    const badgeRegistrationLink =
      'https://registration.experientevent.com/ShowNYN251?flow=attendee&MarketingCode=BULLETINWEB&utm_source=website&utm_medium=referral&utm_campaign=bulletin&utm_content=BULLETINWEB'
    return {
      metaDescription: null,
      descriptionExpanded: false,
      chatMe: null,
      chatOther: null,
      showChatButton: false,
      instagram_followers: null,
      trackingInfo: {
        'Brand name': this.brand.brand_name,
        BuyonBulletin: this.brand.buy_on_bulletin,
        BuyDirect: !this.brand.buy_on_bulletin
      },
      promoCodesExpanded: false,
      dealsAndPromotionsExpanded: false,
      badgeText: `
        NY NOW <br /> 
        Winter 2025 <br /><br />
        <a href="${badgeRegistrationLink}"
          target="_blank" class="underline pointer"> Register </a>
      `,
      EXHIBITOR: 'EXHIBITOR',
      APPLICATION: 'APPLICATION',
      bulletinPick: false,
      buyOnBulletin: false
    }
  },
  metaInfo() {
    return {
      meta: [
        {
          vmid: 'og:title',
          property: 'og:title',
          content: `Bulletin: ${this.brand.brand_name}`
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: this.brand.brand_bio_image_url ? this.brand.brand_bio_image_url : undefined
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: `https://bulletin.co/brands/${this.brandVanityUrlName}`
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.brand.brand_description
        }
      ]
    }
  },

  mounted() {
    if (this.user && this.user.role !== 'ADMIN') {
      this.getUserInfoForChat()
    }
    this.setUpMetaTags()
    this.identifyBrand()
  },
  destroyed() {
    this.restoreMetaTags()
  },

  computed: {
    isPreview() {
      return this.user?.role === 'ADMIN' && !!this.$route.query.preview
    },
    brandBioTrimmed() {
      if (this.brand.brand_description && this.brand.brand_description.length > trimLimit) {
        return this.brand.brand_description.substring(0, trimLimit) + '...'
      }

      return this.brand.brand_description
    },

    brandDescriptionTrim() {
      if (this.brand.description && this.brand.description.length > trimLimit) {
        return this.brand.description.substring(0, trimLimit) + '...'
      }

      return this.brand.description
    },

    brandDescription() {
      return this.brand.brand_description ? this.brand.brand_description : this.brand.description
    },

    brandDescriptionTrimmed() {
      return this.brandBioTrimmed ? this.brandBioTrimmed : this.brandDescriptionTrim
    },

    brandImage() {
      return this.brand.thumbnail_image_url ? this.brand.thumbnail_image_url : this.brand.profile_image_url
    },

    brandName() {
      return this.brand.brand_name ? this.brand.brand_name : this.brand.company_name
    },

    instagramHandle() {
      return this.brand.instagram_url ? this.brand.instagram_url : this.brand.instagram_account_name
    },

    instagramFollowers() {
      return this.brand.instagram_followers ? this.brand.instagram_followers : this.brand.instagram_follower_count
    },

    leadTimeDays() {
      return this.brand.min_lead_time ? this.brand.min_lead_time : this.brand.minimum_lead_time
    },
    leadTimeDaysMax() {
      return this.brand.max_lead_time ? this.brand.max_lead_time : this.brand.maximum_lead_time
    },
    canBioExpand() {
      return this.brand.brand_description && this.brand.brand_description.length > trimLimit
    },
    isRetailerApproved() {
      return this.$store.getters.isRetailerApproved
    },
    showPromoCodeSection() {
      return this.brand && this.brand.published_promo_codes && this.brand.published_promo_codes.length
    },
    showSpecialServicesSection() {
      return this.brand && this.brand.special_services && this.brand.special_services.length
    },
    showDealsAndPromotionsSection() {
      return this.brand && this.brand.deals_and_promotions && this.brand.deals_and_promotions.length
    },
    canPromoCodesToggleBtnVisible() {
      return this.brand.published_promo_codes && this.brand.published_promo_codes.length > 1
    },
    canShowSinglePromoCode() {
      return this.brand.published_promo_codes && this.brand.published_promo_codes.length
    },
    promoCodesToVisible() {
      if (this.canPromoCodesToggleBtnVisible) {
        return this.promoCodesExpanded ? this.brand.published_promo_codes : [this.brand.published_promo_codes[0]]
      }
      if (this.canShowSinglePromoCode) {
        return [this.brand.published_promo_codes[0]]
      }
    },
    canDealsAndPromotionsToggleBtnVisible() {
      return this.brand.deals_and_promotions && this.brand.deals_and_promotions.length
    },
    dealsAndPromotionsToVisible() {
      if (this.canDealsAndPromotionsToggleBtnVisible) {
        return this.dealsAndPromotionsExpanded ? this.brand.deals_and_promotions : []
      }
    },
    isNYN() {
      return this.brand?.trade_shows?.[this.eventId]?.booth_number
    },
    hasTradeShows() {
      return this.brand?.trade_shows?.[this.eventId]?.booth_number
    },
    isExhibitor() {
      return this.brand.source === 'EXHIBITOR'
    },
    canShowAdditionalInfo() {
      const { hasTradeShows, instagramHandle, instagramFollowers, leadTimeDays, brand } = this
      if (!hasTradeShows) return true
      return instagramHandle && instagramFollowers && leadTimeDays && brand.max_lead_time && brand.minimum_order_value
    },
    canShowNyNowBoothNumber() {
      return this.brand?.active_trade_show?.booth_number?.toString().toLowerCase().substring(0, 2) !== 'wl'
    },

    ...mapGetters({
      user: 'userState'
    })
  },

  watch: {
    user() {
      this.getUserInfoForChat()
      this.$forceUpdate()
    }
  },

  methods: {
    getUserInfoForChat,
    isChatButtonVisible,
    sanitizeText,
    setUpMetaTags,
    identifyBrand,
    restoreMetaTags,
    scrollToBrandDetailsMobile,
    startCase(string) {
      return this.$utils.capitalize(string)
    },
    replaceImage() {
      const num = this.brand.id.toString().match(/\d+/)[0][0]
      return new URL(
        `../../../assets/img/nyn-images-event-id-1909/Cloud-${num === 0 || num > 6 ? '3' : num}.jpg`,
        import.meta.url
      ).href
    }
  }
}

async function getUserInfoForChat() {
  const { data } =
    typeof this.user.brand_id === 'undefined'
      ? await MiscApiService.getMeRetailer()
      : await BrandApiService.getBrandMe()
  this.chatMe = {
    fullName: `${data.first_name} from ${data.store_name ? data.store_name : data.brand_name}`,
    role: this.user.role,
    userId: this.user.brand_id ? this.user.brand_id : this.user.retailer_id,
    email: this.user.email
  }

  this.chatOther = {
    storeName: this.brand.brand_name,
    firstName: this.brand.first_name,
    email: this.brand.email,
    userId: this.brand.id,
    role: 'BRAND'
  }

  this.showChatButton = true
}

function isChatButtonVisible() {
  const { thumbnail_image_url, id } = this.brand

  if (!this.user) {
    return
  }

  const { role, brand_id } = this.user
  return this.showChatButton && thumbnail_image_url && this.user && role !== 'ADMIN' && brand_id !== id
}

function sanitizeText(text) {
  const sanitized = sanitizeHtml(text, {
    allowedTags: ['b', 'span', 'em', 'strong', 'a', 'u', 'p', 'br'],
    allowedAttributes: {
      a: ['href']
    }
  })

  if (sanitized === null || sanitized === 'null' || sanitized === undefined || sanitized === 'undefined') {
    return ''
  }

  return sanitized
}

function setUpMetaTags() {
  if (this.brand.brand_description) {
    this.metaDescription = metaTags.getDescription()
    metaTags.setBrandPageDescription(this.brand.brand_name, this.brand.brand_description)
    metaTags.setTitle(`${this.brand.brand_name} Wholesale & Retail on Bulletin.co`)
  }
}

function identifyBrand() {
  this.bulletinPick = this.brand.bulletin_pick ? this.brand.bulletin_pick : false
  this.buyOnBulletin = this.brand.buy_on_bulletin ? this.brand.buy_on_bulletin : false

  this.trackingInfo.BuyonBulletin = this.buyOnBulletin
  this.trackingInfo.BuyDirect = !this.buyOnBulletin
}

function restoreMetaTags() {
  if (this.metaDescription) {
    metaTags.setBrandPageDescription(this.metaDescription)
    metaTags.setTitle(`Bulletin`)
  }
}

function scrollToBrandDetailsMobile() {
  const element = document.querySelector('#brand-details-mobile')
  const bodyRect = document.body.getBoundingClientRect()
  const elemRect = element.getBoundingClientRect()
  const offsetToScroll = elemRect.top - bodyRect.top - 150
  window.scrollTo(0, offsetToScroll)
}
